/**
 * Mapa das unidades FS.
 *
 * A geografia é embutida (`brazil-topology.json`, EPSG:4326 quantizado): o app
 * roda em intranet sem internet, então nada de tile server nem biblioteca de
 * mapas — só um SVG projetado à mão.
 */

import { Minus, Plus, RotateCcw } from "lucide-react";
import * as React from "react";

import topologia from "@/data/brazil-topology.json";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ReferenciaUnidades, Unidade } from "@/types";

const VW = 720;
const VH = 720;
const PAD = 30;

const anel = topologia.ring as [number, number][];
const arcos = topologia.arcs as [number, number][][];
const transform = topologia.transform as { scale: [number, number]; translate: [number, number] };

const BBOX = anel.reduce(
  (acc, [lng, lat]) => ({
    minX: Math.min(acc.minX, lng),
    maxX: Math.max(acc.maxX, lng),
    minY: Math.min(acc.minY, lat),
    maxY: Math.max(acc.maxY, lat),
  }),
  { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity },
);

/** Arcos TopoJSON vêm em deltas quantizados — aqui viram coordenadas absolutas. */
const DIVISAS: [number, number][][] = arcos.map((arco) => {
  let x = 0;
  let y = 0;
  return arco.map(([dx, dy]) => {
    x += dx;
    y += dy;
    return [
      x * transform.scale[0] + transform.translate[0],
      y * transform.scale[1] + transform.translate[1],
    ] as [number, number];
  });
});

const escala = Math.min(
  (VW - 2 * PAD) / (BBOX.maxX - BBOX.minX),
  (VH - 2 * PAD) / (BBOX.maxY - BBOX.minY),
);
const offsetX = (VW - escala * (BBOX.maxX - BBOX.minX)) / 2;
const offsetY = (VH - escala * (BBOX.maxY - BBOX.minY)) / 2;

const projetar = (lng: number, lat: number): [number, number] => [
  offsetX + (lng - BBOX.minX) * escala,
  offsetY + (BBOX.maxY - lat) * escala,
];

const caminho = (pontos: [number, number][], fechar = false) =>
  pontos
    .map(([lng, lat], i) => {
      const [x, y] = projetar(lng, lat);
      return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ") + (fechar ? " Z" : "");

/**
 * O anel nacional tem resolução menor que os arcos das UFs, e os arcos já
 * incluem a fronteira. Traçar contorno nos dois deixava o litoral com duas
 * linhas desencontradas — por isso o anel entra só como preenchimento e todo o
 * traçado (fronteira e divisas) vem dos arcos, numa espessura só.
 */
const CAMINHO_BR = caminho(anel, true);
const CAMINHOS_DIVISAS = DIVISAS.map((pontos) => caminho(pontos));

export function corStatus(valor: string) {
  const texto = String(valor ?? "").toUpperCase();
  if (texto === "OPERANDO" || texto === "OK" || texto.includes("- OK")) return "#1E7A45";
  if (texto.includes("NOK")) return "#C0392B";
  return "#B7791F";
}

interface Props {
  dados: ReferenciaUnidades;
  selecionada: Unidade | null;
  onSelecionar: (unidade: Unidade) => void;
}

export function MapaOperacoes({ dados, selecionada, onSelecionar }: Props) {
  const [empresas, setEmpresas] = React.useState<Record<string, boolean>>({
    A: true,
    B: true,
    C: true,
  });
  const [situacoes, setSituacoes] = React.useState<Record<string, boolean>>({
    OPERANDO: true,
    "NÃO OPERANTE": true,
  });
  const [vista, setVista] = React.useState({ z: 1, tx: 0, ty: 0 });
  const arrasto = React.useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);

  // As coordenadas vêm do backend por cidade; unidades na mesma cidade são
  // espalhadas em círculo para não virarem um ponto só.
  const pontos = React.useMemo(() => {
    const porCidade = new Map<string, Unidade[]>();
    for (const unidade of dados.unidades) {
      const chave = `${unidade.cidade}|${unidade.uf}`;
      porCidade.set(chave, [...(porCidade.get(chave) ?? []), unidade]);
    }
    return dados.unidades.map((unidade) => {
      const chave = `${unidade.cidade}|${unidade.uf}`;
      const grupo = porCidade.get(chave) ?? [unidade];
      const base = dados.coordenadas[chave] ?? [-15.78, -47.93];
      const i = grupo.indexOf(unidade);
      if (grupo.length === 1) return { unidade, lat: base[0], lng: base[1] };
      const angulo = (2 * Math.PI * i) / grupo.length;
      return {
        unidade,
        lat: base[0] + 0.22 * Math.sin(angulo),
        lng: base[1] + 0.22 * Math.cos(angulo),
      };
    });
  }, [dados]);

  const visiveis = pontos.filter(
    ({ unidade }) => empresas[unidade.emp] && situacoes[unidade.sit],
  );
  const operando = visiveis.filter(({ unidade }) => unidade.sit === "OPERANDO").length;

  const zoom = (fator: number) =>
    setVista((v) => {
      const nz = Math.max(1, Math.min(7, v.z * fator));
      const c = VW / 2;
      return { z: nz, tx: c - (c - v.tx) * (nz / v.z), ty: c - (c - v.ty) * (nz / v.z) };
    });

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_21rem]">
      <section className="cartao overflow-hidden">
        <header className="flex flex-wrap items-center gap-2 border-b border-border/70 px-4 py-3">
          <span className="rotulo-seco mr-1">Empresa</span>
          {(["A", "B", "C"] as const).map((chave) => (
            <button
              key={chave}
              type="button"
              aria-pressed={empresas[chave]}
              onClick={() => setEmpresas((e) => ({ ...e, [chave]: !e[chave] }))}
              className={cn(
                "flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-bold",
                "transition-all duration-200 ease-suave hover:-translate-y-px",
                empresas[chave]
                  ? "text-fs-escuro shadow-baixa"
                  : "border-border text-muted-foreground hover:border-primary/35",
              )}
              style={
                empresas[chave]
                  ? {
                      borderColor: `${dados.empresas[chave]?.cor}66`,
                      background: `${dados.empresas[chave]?.cor}14`,
                    }
                  : undefined
              }
            >
              <span
                className="size-2.5 rounded-full transition-colors"
                style={{
                  background: empresas[chave] ? dados.empresas[chave]?.cor : "#CBD3C8",
                }}
              />
              {dados.empresas[chave]?.curto}
            </button>
          ))}

        </header>

        {/*
          O SVG é quadrado (720x720). Sem teto de altura ele acompanhava a
          largura da coluna e tomava a tela inteira num monitor grande; aqui a
          altura é limitada e o mapa fica centralizado dentro dela.
        */}
        <div className="bg-[linear-gradient(180deg,#FBFCFA,#F3F6F1)] p-3">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VW} ${VH}`}
            className="mx-auto block h-[min(52vh,29rem)] w-full max-w-[29rem] cursor-grab touch-none active:cursor-grabbing"
            onPointerDown={(e) => {
              arrasto.current = { x: e.clientX, y: e.clientY, tx: vista.tx, ty: vista.ty };
            }}
            onPointerMove={(e) => {
              const atual = arrasto.current;
              const caixa = svgRef.current?.getBoundingClientRect();
              if (!atual || !caixa) return;
              setVista((v) => ({
                ...v,
                tx: atual.tx + (e.clientX - atual.x) * (VW / caixa.width),
                ty: atual.ty + (e.clientY - atual.y) * (VH / caixa.height),
              }));
            }}
            onPointerUp={() => {
              arrasto.current = null;
            }}
            onPointerLeave={() => {
              arrasto.current = null;
            }}
          >
            <defs>
              <linearGradient id="mapa-terra" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#EAF0E7" />
                <stop offset="100%" stopColor="#DEE7DB" />
              </linearGradient>
              <filter id="mapa-sombra" x="-12%" y="-12%" width="124%" height="124%">
                <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#0F3325" floodOpacity="0.13" />
              </filter>
            </defs>

            <g transform={`translate(${vista.tx} ${vista.ty}) scale(${vista.z})`}>
              {/* Só o preenchimento: o contorno inteiro vem dos arcos abaixo. */}
              <path d={CAMINHO_BR} fill="url(#mapa-terra)" stroke="none" filter="url(#mapa-sombra)" />
              {CAMINHOS_DIVISAS.map((d, i) => (
                <path
                  key={i}
                  d={d}
                  fill="none"
                  stroke="#A7B7A2"
                  strokeWidth={0.9}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {visiveis.map(({ unidade, lat, lng }) => {
                const [x, y] = projetar(lng, lat);
                const ativo =
                  selecionada?.centro === unidade.centro && selecionada.emp === unidade.emp;
                return (
                  <g
                    key={`${unidade.emp}-${unidade.centro}`}
                    transform={`translate(${x} ${y})`}
                    className="cursor-pointer"
                    onClick={() => onSelecionar(unidade)}
                  >
                    {ativo && (
                      <circle
                        r={15 / vista.z}
                        fill={dados.empresas[unidade.emp]?.cor}
                        opacity={0.2}
                        className="animate-brilho"
                      />
                    )}
                    <circle
                      r={ativo ? 7.5 / vista.z : 5 / vista.z}
                      fill={dados.empresas[unidade.emp]?.cor}
                      stroke="#fff"
                      strokeWidth={1.8 / vista.z}
                      opacity={unidade.sit === "OPERANDO" ? 1 : 0.5}
                    />
                    <title>{`${unidade.cidade}/${unidade.uf} · ${unidade.local} · ${unidade.sit}`}</title>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>

        {/*
          Rodapé no mesmo desenho do cartão do fluxo: os controles (filtro de
          situação e zoom) numa faixa sob o desenho, e a contagem embaixo. No
          topo ficam só os filtros de empresa, que definem o que o mapa mostra.
        */}
        <footer className="space-y-2.5 border-t border-border/70 bg-secondary/40 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rotulo-seco mr-1">Situação</span>
              {Object.keys(situacoes).map((chave) => (
                <button
                  key={chave}
                  type="button"
                  aria-pressed={situacoes[chave]}
                  onClick={() => setSituacoes((s) => ({ ...s, [chave]: !s[chave] }))}
                  className={cn(
                    "rounded-full border px-3 py-1 text-[11px] font-bold",
                    "transition-all duration-200 ease-suave hover:-translate-y-px",
                    situacoes[chave]
                      ? "border-primary/40 bg-accent text-fs-escuro shadow-baixa"
                      : "border-border bg-card text-muted-foreground hover:border-primary/35",
                  )}
                >
                  {chave}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1 rounded-lg border border-border/70 bg-card p-0.5">
              <Button
                size="icon"
                variant="ghost"
                className="size-8 hover:bg-secondary"
                onClick={() => zoom(1.3)}
                aria-label="Aproximar"
              >
                <Plus />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="size-8 hover:bg-secondary"
                onClick={() => zoom(1 / 1.3)}
                aria-label="Afastar"
              >
                <Minus />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="size-8 hover:bg-secondary"
                onClick={() => setVista({ z: 1, tx: 0, ty: 0 })}
                aria-label="Recentralizar"
              >
                <RotateCcw />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
            <span>
              <b className="numerico text-fs-escuro">{visiveis.length}</b> unidades visíveis
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-emerald-600" />
              <b className="numerico text-fs-escuro">{operando}</b> operando
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-muted-foreground/40" />
              <b className="numerico text-fs-escuro">{visiveis.length - operando}</b> não operantes
            </span>
            <span className="ml-auto hidden sm:inline">
              Arraste para mover · use + / − para o zoom
            </span>
          </div>
        </footer>
      </section>

      <aside className="cartao h-fit p-5 lg:sticky lg:top-[4.5rem]">
        {selecionada ? (
          <div className="animate-surgir space-y-4 text-sm">
            <div>
              <Badge
                className="ring-transparent"
                style={{ background: dados.empresas[selecionada.emp]?.cor, color: "#fff" }}
              >
                {dados.empresas[selecionada.emp]?.curto}
              </Badge>
              <h3 className="mt-2.5 text-lg font-extrabold leading-tight tracking-tight text-fs-escuro">
                {selecionada.cidade}
                <span className="text-muted-foreground"> / {selecionada.uf}</span>
              </h3>
              <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                {selecionada.tipo} · centro{" "}
                <span className="numerico text-fs-escuro">{selecionada.centro}</span> ·{" "}
                {selecionada.local}
              </p>
            </div>

            <dl className="grid grid-cols-[5rem_1fr] gap-x-3 gap-y-2 border-y border-border/70 py-3 text-xs">
              <dt className="rotulo-seco">CNPJ</dt>
              <dd className="numerico">{selecionada.cnpj}</dd>
              <dt className="rotulo-seco">IE</dt>
              <dd className="numerico">{selecionada.ie}</dd>
              <dt className="rotulo-seco">Endereço</dt>
              <dd className="leading-relaxed">{selecionada.end}</dd>
            </dl>

            <div className="grid grid-cols-2 gap-2">
              {(["sala", "anp", "tanc", "sefaz"] as const).map((campo) => {
                const cor = corStatus(selecionada[campo]);
                return (
                  <div
                    key={campo}
                    className="rounded-lg border border-border/70 bg-secondary/40 p-2.5 transition-colors hover:bg-secondary/80"
                    style={{ borderLeftColor: cor, borderLeftWidth: 3 }}
                  >
                    <div className="rotulo-seco">
                      {campo === "tanc" ? "Tancagem" : campo.toUpperCase()}
                    </div>
                    <div className="mt-0.5 text-xs font-bold leading-tight" style={{ color: cor }}>
                      {selecionada[campo]}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="py-6 text-center">
            <div
              aria-hidden
              className="mx-auto mb-3 grid size-11 place-items-center rounded-full bg-accent text-lg"
            >
              📍
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Clique em um ponto do mapa para ver CNPJ, IE, endereço e o status de Sala, ANP,
              Tancagem e SEFAZ da unidade.
            </p>
          </div>
        )}
      </aside>
    </div>
  );
}
